import React, { useCallback, useState } from 'react';
import MeetingAgendaModal from '../components/MeetingAgendaModal';
import {
  buildUserCalendlyPrefill,
  openCalendlyPopup,
} from '../utils/calendlyWidget';
import {
  createDefaultAgendaRows,
  formatAgendaForCalendly,
} from '../utils/meetingAgenda';

/**
 * Shared flow: Meeting Agenda modal → Calendly (or fallback URL) with name/email + a1 agenda.
 *
 * @param {{
 *   currentUser?: import('firebase/auth').User | null;
 *   userProfile?: Record<string, unknown> | null;
 *   disabled?: boolean;
 *   variant?: 'roadmap' | 'department';
 *   requireFocus?: boolean;
 * }} options
 */
export function useStrategyCallBooking({
  currentUser,
  userProfile,
  disabled = false,
  variant = 'department',
  requireFocus = true,
} = {}) {
  const [agendaOpen, setAgendaOpen] = useState(false);
  const [pendingUrl, setPendingUrl] = useState(null);
  const [pendingFallbackUrl, setPendingFallbackUrl] = useState(null);
  const [initialRows, setInitialRows] = useState(() => createDefaultAgendaRows());
  const [useCalendlyOnConfirm, setUseCalendlyOnConfirm] = useState(true);

  const closeAgenda = useCallback(() => {
    setAgendaOpen(false);
    setPendingUrl(null);
    setPendingFallbackUrl(null);
  }, []);

  /**
   * @param {{ url: string, initialFocus?: string, fallbackUrl?: string, useCalendly?: boolean }} params
   */
  const openBooking = useCallback(
    ({ url, initialFocus = '', fallbackUrl, useCalendly = true }) => {
      if (disabled || !url) return;

      setPendingUrl(url);
      setPendingFallbackUrl(fallbackUrl || null);
      setUseCalendlyOnConfirm(useCalendly !== false);
      setInitialRows(createDefaultAgendaRows({ focus: initialFocus }));
      setAgendaOpen(true);
    },
    [disabled]
  );

  const confirmAgenda = useCallback(
    (rows) => {
      const agendaText = formatAgendaForCalendly(rows);
      const prefill = buildUserCalendlyPrefill(currentUser, userProfile);

      closeAgenda();

      if (useCalendlyOnConfirm && pendingUrl) {
        openCalendlyPopup(pendingUrl, prefill, { agendaText });
      } else if (pendingFallbackUrl) {
        window.open(pendingFallbackUrl, '_blank');
      } else if (pendingUrl) {
        openCalendlyPopup(pendingUrl, prefill, { agendaText });
      }
    },
    [
      closeAgenda,
      currentUser,
      pendingFallbackUrl,
      pendingUrl,
      useCalendlyOnConfirm,
      userProfile,
    ]
  );

  const meetingAgendaModal = (
    <MeetingAgendaModal
      isOpen={agendaOpen}
      onClose={closeAgenda}
      onConfirm={confirmAgenda}
      initialRows={initialRows}
      requireFocus={requireFocus}
      variant={variant}
    />
  );

  return {
    openBooking,
    closeAgenda,
    meetingAgendaModal,
    agendaOpen,
  };
}
